import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const SubscriptionContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FREE_ARTICLES_PER_DAY = 3;

export const SubscriptionProvider = ({ children }) => {
  const { user, token } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [trialStatus, setTrialStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [articlesViewed, setArticlesViewed] = useState(() => {
    const stored = localStorage.getItem('wt_articles_viewed');
    if (stored) {
      const data = JSON.parse(stored);
      const today = new Date().toISOString().split('T')[0];
      if (data.date === today) {
        return data.articles;
      }
    }
    return [];
  });

  const fetchSubscription = useCallback(async () => {
    if (!token) {
      setSubscription(null);
      setTrialStatus({
        logged_in: false,
        trial_active: false,
        is_premium: false,
        tier: 'anonymous',
        features: []
      });
      setLoading(false);
      return;
    }

    try {
      const [subResponse, trialResponse] = await Promise.all([
        axios.get(`${API}/subscription/my-subscription`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/subscription/trial-status`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setSubscription(subResponse.data);
      setTrialStatus(trialResponse.data);
    } catch (error) {
      console.error('Failed to fetch subscription:', error);
      setSubscription(null);
      setTrialStatus(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  // Derived states
  const isPremium = subscription?.is_premium || false;
  const isTrialActive = trialStatus?.trial_active || false;
  const isTrialExpired = trialStatus?.trial_expired || false;
  const trialRemainingSeconds = trialStatus?.trial_remaining_seconds || 0;
  const tier = trialStatus?.tier || subscription?.tier || 'free';
  const features = trialStatus?.features || subscription?.features || [];

  // Check if user has full access (premium or trial)
  const hasFullAccess = isPremium || isTrialActive;

  // Check if a specific feature is available
  const hasFeature = useCallback((featureName) => {
    if (isPremium || isTrialActive) return true;
    return features.includes(featureName);
  }, [isPremium, isTrialActive, features]);

  const checkArticleAccess = useCallback(async (articleId) => {
    // Premium or trial users always have access
    if (isPremium || isTrialActive) {
      return { canAccess: true, remaining: -1, tier };
    }

    // For logged-in users, check via API
    if (token) {
      try {
        const response = await axios.post(`${API}/articles/check-access`, 
          { article_id: articleId },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        return {
          canAccess: response.data.can_access,
          remaining: response.data.remaining,
          message: response.data.message,
          tier: response.data.tier,
          trialExpired: response.data.trial_expired
        };
      } catch (error) {
        console.error('Failed to check access:', error);
        return { canAccess: true, remaining: FREE_ARTICLES_PER_DAY };
      }
    }

    // For anonymous users, track in localStorage
    const today = new Date().toISOString().split('T')[0];
    const stored = localStorage.getItem('wt_articles_viewed');
    let viewedArticles = [];
    
    if (stored) {
      const data = JSON.parse(stored);
      if (data.date === today) {
        viewedArticles = data.articles;
      }
    }

    // Already viewed this article
    if (viewedArticles.includes(articleId)) {
      return { 
        canAccess: true, 
        remaining: FREE_ARTICLES_PER_DAY - viewedArticles.length,
        tier: 'anonymous'
      };
    }

    // Check if limit reached
    if (viewedArticles.length >= FREE_ARTICLES_PER_DAY) {
      return { 
        canAccess: false, 
        remaining: 0,
        message: 'Daily limit reached. Register for 48h FREE full access!',
        tier: 'anonymous'
      };
    }

    // Record the view
    viewedArticles.push(articleId);
    localStorage.setItem('wt_articles_viewed', JSON.stringify({
      date: today,
      articles: viewedArticles
    }));
    setArticlesViewed(viewedArticles);

    return { 
      canAccess: true, 
      remaining: FREE_ARTICLES_PER_DAY - viewedArticles.length,
      tier: 'anonymous'
    };
  }, [isPremium, isTrialActive, token, tier]);

  const getRemainingArticles = useCallback(() => {
    if (isPremium || isTrialActive) return -1; // Unlimited
    
    const today = new Date().toISOString().split('T')[0];
    const stored = localStorage.getItem('wt_articles_viewed');
    
    if (stored) {
      const data = JSON.parse(stored);
      if (data.date === today) {
        return FREE_ARTICLES_PER_DAY - data.articles.length;
      }
    }
    
    return FREE_ARTICLES_PER_DAY;
  }, [isPremium, isTrialActive]);

  const refreshSubscription = () => {
    fetchSubscription();
  };

  return (
    <SubscriptionContext.Provider value={{
      subscription,
      trialStatus,
      isPremium,
      isTrialActive,
      isTrialExpired,
      trialRemainingSeconds,
      tier,
      features,
      hasFullAccess,
      hasFeature,
      loading,
      articlesViewed,
      checkArticleAccess,
      getRemainingArticles,
      refreshSubscription,
      FREE_ARTICLES_PER_DAY
    }}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within SubscriptionProvider');
  }
  return context;
};
